import React from 'react';
import PropTypes from 'prop-types';

export const createRendererActions = ({
    items,
    setItems,
    edit = false,
    captureItemPositions,
}) => {
    const editJSONItem = (index, item) => {
        if (!edit || !Array.isArray(items)) {
            return;
        }

        const nextItems = [...items];
        nextItems[index] = item;
        setItems(nextItems);
    };

    const addItem = (itemJSON, insertAtIndex = null) => {
        if (!edit || !Array.isArray(items)) {
            return;
        }

        const nextItems = [...items];
        const nextItem = itemJSON;

        if (insertAtIndex === 0) {
            nextItems.unshift(nextItem);
        } else if (Number.isInteger(insertAtIndex)) {
            const boundedIndex = Math.max(0, Math.min(insertAtIndex, nextItems.length));
            nextItems.splice(boundedIndex, 0, nextItem);
        } else {
            nextItems.push(nextItem);
        }

        setItems(nextItems);
    };

    const removeItem = (index) => {
        if (!edit || !Array.isArray(items)) {
            return;
        }

        if (typeof captureItemPositions === 'function') {
            captureItemPositions();
        }

        const nextItems = [...items];
        nextItems.splice(index, 1);
        setItems(nextItems);
    };

    const swapItems = (index1, index2) => {
        if (!edit || !Array.isArray(items)) {
            return;
        }

        const nextItems = [...items];
        if (index1 < 0 || index1 >= nextItems.length) {
            return;
        }

        const boundedTargetIndex = Math.max(0, Math.min(index2, nextItems.length - 1));
        if (boundedTargetIndex === index1) {
            return;
        }

        if (typeof captureItemPositions === 'function') {
            captureItemPositions();
        }

        [nextItems[index1], nextItems[boundedTargetIndex]] = [nextItems[boundedTargetIndex], nextItems[index1]];
        setItems(nextItems);

    };

    const moveItem = (fromIndex, toIndex) => {
        if (!edit || !Array.isArray(items)) {
            return;
        }

        captureItemPositions?.();
        const nextItems = [...items];
        if (fromIndex < 0 || fromIndex >= nextItems.length) {
            return;
        }

        const boundedTargetIndex = Math.max(0, Math.min(toIndex, nextItems.length - 1));
        const [movedItem] = nextItems.splice(fromIndex, 1);
        nextItems.splice(boundedTargetIndex, 0, movedItem);
        setItems(nextItems);
    };

    return {
        editJSONItem,
        addItem,
        removeItem,
        swapItems,
        moveItem,
    };
};

const Renderer = ({
    item,
    index,
    itemKey,
    edit = false,
    dragEnabled = false,
    dragClassName = '',
    setItemRef,
    onDragOver,
    onDrop,
    renderEditItem,
    renderViewItem,
}) => {
    if (!item || !item.type) {
        console.warn(`Item at index ${index} is missing type or is null:`, item);
        return null;
    }

    const renderContent = () => {

        return edit ? renderEditItem(item, index) : renderViewItem(item, index);
    };

    if (edit) {
        return (
            <div
                ref={(element) => {
                    if (typeof setItemRef === 'function') {
                        setItemRef(itemKey, element);
                    }
                }}
                className={`${dragEnabled ? 'draggable-item' : ''} ${dragClassName}`.trim()}
                {...(dragEnabled ? {
                    onDragOver: (event) => onDragOver(event, index),
                    onDrop: (event) => onDrop(event, index),
                } : {})}
            >
                {renderContent()}
            </div>
        );
    }

    return renderContent();
};

Renderer.propTypes = {
    item: PropTypes.object,
    index: PropTypes.number.isRequired,
    itemKey: PropTypes.string,
    edit: PropTypes.bool,
    dragEnabled: PropTypes.bool,
    dragClassName: PropTypes.string,
    setItemRef: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    renderEditItem: PropTypes.func,
    renderViewItem: PropTypes.func,
};

export default Renderer;
